import axios, { AxiosError, AxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE ?? '/api';

export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  // Repeat keys for arrays (?genre=a&genre=b) so Express's default simple
  // query parser turns them into an array. With brackets ([]) the simple
  // parser keeps "genre[]" as a literal key and ValidationPipe rejects it.
  paramsSerializer: { indexes: null },
});

let refreshPromise: Promise<unknown> | null = null;

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!original) return Promise.reject(err);

    if (err.response?.status === 401 && !original._retry && !original.url?.includes('/auth/')) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${baseURL}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => {
              refreshPromise = null;
            });
        }
        await refreshPromise;
        return api(original);
      } catch (refreshErr) {
        // A failed refresh only means "the session is gone". Only force a
        // bounce to /login when the user is actually on a protected page —
        // otherwise an anonymous visitor on a public page (e.g. the boot-time
        // /users/me probe firing on the homepage after sign-out) would get
        // redirected to /login on every hard refresh. Public-page callers
        // catch the rejection and fall back to the anonymous state.
        const { router } = await import('../router');
        const current = router.currentRoute.value;
        if (current.meta.requiresAuth && current.name !== 'login') {
          router.replace({ name: 'login', query: { next: current.fullPath } });
        }
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(err);
  },
);
